import {
  DEFAULT_CLAUSE_SOURCE,
  DEFAULT_DELAY_CAP_BP,
  DEFAULT_DELAY_RATE_BP,
  DEFAULT_LATE_INTEREST_RATE_BP,
  DEFAULT_PAYMENT_PLAN,
  DEFAULT_PENALTY_RATE_BP,
  DEFAULT_WARRANTY_DEPOSIT_BP,
  DEFAULT_WARRANTY_MONTHS,
  DEFAULT_WARRANTY_TERMS,
} from "../contract/defaults";
import { BaseRow, OrgScopedRow } from "../common/types";
import { DEFAULT_VAT_MODE, VatMode } from "../money/vat";
import { DEFAULT_QUOTE_MARGIN_BP } from "../template/price-margin";
import type { OrgStatus } from "./status";

export const MEMBER_ROLES = ["owner", "staff"] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export const ROLE_LABEL: Record<MemberRole, string> = {
  owner: "대표",
  staff: "실장",
};

export interface Org extends BaseRow {
  name: string;
  slug: string;
  address: string;
  tel: string;
  /**
   * 외부 연동(홈페이지 폼 웹훅)이 쓰는 API 키의 sha256 해시. 평문은 발급 순간에만
   * 존재하고 저장하지 않는다 — 저장소가 털려도 키로 쓸 수 없어야 한다.
   * null 이면 아직 발급한 적이 없다(어떤 키로도 통과하지 못한다).
   */
  api_key_hash: string | null;
  // 이용 상태(`0086`) — 접근 수준은 `orgAccessOf` 가 바뀐 시각으로 계산한다 · 운영자 콘솔만 바꾼다.
  status: OrgStatus;
  status_changed_at: string | null;
  status_note: string | null;
  // 테스트 표시·시연 조직(`0088`) — 운영자 콘솔만 바꾼다 · demo_created_at 은 시연 만들기 RPC 만 채운다.
  is_test: boolean;
  demo_created_at: string | null;
  demo_reset_at: string | null;
}

export interface Member extends OrgScopedRow {
  /**
   * 로그인 계정(`auth.users.id`). RLS 헬퍼가 `member.user_id = auth.uid()` 로
   * 조직 소속을 판정하므로 **DB 에서는 not null** 이다. 지금 로그인은 멤버를 고르는
   * 목이라 인메모리 저장소는 멤버 id 를 딴 임시 값을 넣어 둔다.
   */
  user_id: string;
  name: string;
  role: MemberRole;
  email: string | null;
  phone: string | null;
  // 간편접수 담당 선택 목록에 보일지(`0029`).
  intake_visible: boolean;
  // 간편접수에 이름과 함께 보이는 짧은 소개(최대 `MEMBER_INTRO_MAX` 자).
  intro: string | null;
}

export const MEMBER_INTRO_MAX = 60;
// 상호 상한 — 설정 회사 정보와 시공사 만들기가 같이 쓴다.
export const COMPANY_NAME_MAX = 60;
// 상호 검사 규칙 — 시공사 만들기와 운영자 시연 만들기가 같이 쓴다(주소 꼴 상호가 있어 URL 은 막지 않는다).
export const COMPANY_NAME_RULE = { label: "시공사 이름", max: COMPANY_NAME_MAX, url: false } as const;

export const INVITE_STATUSES = ["PENDING", "ACCEPTED", "EXPIRED"] as const;
export type InviteStatus = (typeof INVITE_STATUSES)[number];

export interface Invite extends OrgScopedRow {
  email: string;
  role: MemberRole;
  token: string;
  status: InviteStatus;
  /**
   * 초대한 멤버. SQL 은 `on delete set null` 이라(초대를 보낸 사람이 조직을 떠나도
   * 초대 기록은 남는다) **null 이 될 수 있다**.
   */
  invited_by: string | null;
  expires_at: string;
  accepted_at: string | null;
  /**
   * 회수 시각. 회수하지 않았으면 null.
   *
   * 「회수됨」 과 「기한 만료」 는 화면 문구가 다른데, 시각 자국(`expires_at === updated_at`)
   * 으로 구분하면 이관 후 전부 만료로 보인다 — DB 트리거가 `updated_at` 을 자기 시계로
   * 덮기 때문이다. 그래서 열로 남긴다.
   */
  revoked_at: string | null;
}

/**
 * 조직 설정. 값은 문자열 하나 — 관리비율·기본 조건문 같은 것들이라
 * 표를 키마다 나누지 않고 키·값으로 둔다.
 */
export interface OrgSetting extends OrgScopedRow {
  key: OrgSettingKey;
  value: string;
}

export const ORG_SETTING_KEYS = [
  "mgmt_fee_rate",
  "quote_valid_days",
  "default_conditions",
  "extra_notice",
  "company_name",
  "company_address",
  "company_tel",
  "logo_url",
  "stamp_url",
  "codes.residence",
  "codes.area_band",
  "codes.space_state",
  "codes.space",
  "codes.lost_reason",
  "notify",
  // ── 계약서 (4단계) ─────────────────────────────────────────────
  //
  // 저장소 `create`(묶음 5)가 여기서 복사해 계약 행에 넣는다. **설정은 기본값의
  // 출처, 계약은 그 계약의 출처**라 `clause_source` 가 양쪽에 다 있다 — 계약에서
  // 고치면 **그 사본만** `CUSTOM` 이 된다(`clauseSourceAfterEdit`).
  "contract.clauses",
  "contract.clause_source",
  "contract.payments",
  "contract.delay_rate_bp",
  "contract.delay_cap_bp",
  // 공정위 서식이 쓰는 칸. 지체상금과 **다른 수**라 칸을 나눴다(제6조).
  "contract.late_interest_rate_bp",
  "contract.penalty_rate_bp",
  "contract.warranty_months",
  // **기본값이 여기 없으면 열이 있어도 닿지 않는다.** 저장소 `create` 가 설정에서
  // 복사하는데 키가 없으면 모든 계약이 DB default(`'[]'`·0)로 생기고, 공정위
  // 제10조의 「창호 2년」 예외가 조용히 사라진다(2026-09-22 리뷰).
  "contract.warranty_terms",
  "contract.warranty_deposit_bp",
  // 설정 화면에서 고른 서식(FTC_10079·MOLIT). clause_source 가 CUSTOM 이어도 되돌릴 원문을 안다.
  "contract.clause_base",
  // 기본 특약. 조항(clauses)과 다른 칸이다.
  "contract.special_terms",
  // 계약서에 찍히는 수급인 정보. `company_name`·`company_address`·`company_tel`
  // 과 한 벌이지만 견적서에는 안 쓰여서 지금 생긴다.
  "company_biz_no",
  "company_ceo",
  "company_const_license_no",
  // 준공 대화상자에서 「유지보수」를 먼저 고를지(`"true"`|`"false"`).
  "project.warranty_enabled",
  // 간편접수에서 고객이 담당 디자이너를 고를 수 있게 할지(`"true"`|`"false"`).
  "intake.designer_choice",
  // 로그인 없는 공개 시공사례 페이지(`/s/portfolio/*`)를 열지(`"true"`|`"false"`). 기본 끔.
  "portfolio.public_page",
  // 공개 시공사례 화면의 「상담 문의」 버튼을 보일지(`"true"`|`"false"`). 기본 켬 — 간편접수 주소는 막지 않는다.
  "portfolio.public_inquiry",
  // 고객 알림에서 끈 사건(JSON 배열) — 끈 것만 적어서 새 사건은 기본으로 켜진다.
  "customer_notify.off",
  // 응대 전 문의를 경고할 기준 시간(`"off"`|`"1"`|`"3"`|`"24"`). 기본 3시간.
  "intake.unanswered_hours",
  // 간편접수 폼 모드(`"simple"`|`"detail"`). 상세면 공사 종류·예산대·통화 가능 시간 칸이 붙는다.
  "intake.mode",
  // 고객 화면 하단에 「buildpy로 관리되는 현장입니다」 한 줄을 보일지(`"true"`|`"false"`). 기본 켬 — `"false"` 일 때만 끈다.
  "brand.footer",
  // 새 견적의 부가세 방식 기본값(`"INCL"`|`"EXCL"`) — 만들 때 견적에 복사한다(가견적은 포함 고정).
  "quote_vat_mode",
  // 단가표 회사 기본 마진(만분율 정수, `"2000"` = +20%) — 원가가 있는 품목의 판매가를 낸다.
  "quote_margin_bp",
  // 공종별 마진(JSON `{공종코드: 만분율}`) — 품목 마진이 없을 때 회사 기본보다 먼저 쓴다.
  "quote_trade_margins",
  // 직원에게 손익 탭·실제 원가를 보일지(`"true"`|`"false"`) — 대표는 늘 본다 · DB 0104 정책도 이 값을 읽는다.
  "cost.staff_view",
  // 직원도 단가표·표준 상품 제출·실제 원가를 고칠지(`"true"`|`"false"`) — 실제 원가는 보기도 켜져야 한다(0104).
  "cost.staff_edit",
  // 회사 보정 — 기능공 수(정수 ≥1 · 비우면 항목 표준 기능공 수 · 보통인부는 표준 그대로) · 숙련 계수(0.5~3 · 비우면 1) — 시공일정 면적 초안(#171).
  "process.crew_size",
  "process.factor",
] as const;
export type OrgSettingKey = (typeof ORG_SETTING_KEYS)[number];

// 로그인 없는 화면(간편접수·공개 링크)이 admin 으로 읽어도 되는 설정만 — `notify`(웹훅 URL) 등은 넣지 않는다.
export const PUBLIC_SETTING_KEYS = [
  "intake.designer_choice",
  "intake.mode",
  "codes.residence",
  "codes.area_band",
  // 공개 시공사례 상세의 공간별 전/후 쌍 라벨·순서.
  "codes.space",
  "portfolio.public_page",
  "portfolio.public_inquiry",
  "contract.warranty_months",
  "company_const_license_no",
  "brand.footer",
  // 고객 화면(견적 링크·간편접수)의 조직 표시명.
  "company_name",
] as const satisfies readonly OrgSettingKey[];
export type PublicSettingKey = (typeof PUBLIC_SETTING_KEYS)[number];

export const isPublicSettingKey = (key: string): key is PublicSettingKey =>
  (PUBLIC_SETTING_KEYS as readonly string[]).includes(key);

/**
 * 값이 없을 때 쓰는 기본값. 회사 정보는 org 행(name·address·tel)이 원본이라
 * 빈 문자열로 두고, 화면이 설정값 → org 행 순으로 읽는다.
 * 목록형(조건문·코드값)은 JSON 배열 문자열 한 칸에 담는다.
 */
export const ORG_SETTING_DEFAULT: Record<OrgSettingKey, string> = {
  mgmt_fee_rate: "0.04",
  quote_valid_days: "30",
  default_conditions: "[]",
  extra_notice: "",
  company_name: "",
  company_address: "",
  company_tel: "",
  logo_url: "",
  stamp_url: "",
  "codes.residence": "[]",
  "codes.area_band": "[]",
  "codes.space_state": "[]",
  "codes.space": "[]",
  "codes.lost_reason": "[]",
  notify: "[]",
  // **빈 배열이 「템플릿 그대로」다.** 조항 원문을 설정에 복사해 두면 템플릿을
  // 고쳐도 기존 조직에는 영원히 안 닿는다 — 회사가 실제로 편집했을 때만 값이 찬다.
  "contract.clauses": "[]",
  "contract.clause_source": DEFAULT_CLAUSE_SOURCE,
  "contract.payments": JSON.stringify(DEFAULT_PAYMENT_PLAN),
  "contract.delay_rate_bp": String(DEFAULT_DELAY_RATE_BP),
  "contract.delay_cap_bp": String(DEFAULT_DELAY_CAP_BP),
  "contract.late_interest_rate_bp": String(DEFAULT_LATE_INTEREST_RATE_BP),
  "contract.penalty_rate_bp": String(DEFAULT_PENALTY_RATE_BP),
  "contract.warranty_months": String(DEFAULT_WARRANTY_MONTHS),
  "contract.warranty_terms": JSON.stringify(DEFAULT_WARRANTY_TERMS),
  "contract.warranty_deposit_bp": String(DEFAULT_WARRANTY_DEPOSIT_BP),
  "contract.clause_base": DEFAULT_CLAUSE_SOURCE,
  "contract.special_terms": "[]",
  company_biz_no: "",
  company_ceo: "",
  company_const_license_no: "",
  "project.warranty_enabled": "false",
  "intake.designer_choice": "false",
  "portfolio.public_page": "false",
  "portfolio.public_inquiry": "true",
  "customer_notify.off": "[]",
  "intake.unanswered_hours": "3",
  "intake.mode": "simple",
  "brand.footer": "true",
  quote_vat_mode: DEFAULT_VAT_MODE,
  quote_margin_bp: String(DEFAULT_QUOTE_MARGIN_BP),
  quote_trade_margins: "{}",
  "cost.staff_view": "false",
  "cost.staff_edit": "false",
  "process.crew_size": "",
  "process.factor": "",
};

// 회사 기본 부가세 방식 읽기 — 모르는 값은 포함으로 떨어진다.
export const quoteVatModeOf = (value: string | null | undefined): VatMode =>
  value === "EXCL" ? "EXCL" : DEFAULT_VAT_MODE;

export const isOrgSettingKey = (value: unknown): value is OrgSettingKey =>
  typeof value === "string" &&
  (ORG_SETTING_KEYS as readonly string[]).includes(value);

/**
 * 숫자 설정을 읽는다. 읽을 수 없는 값만 기본값으로 떨어진다.
 *
 * `Number(value) || 기본값` 으로 두면 **0 이 기본값으로 되살아난다** — 관리비율 0% 를
 * 저장한 조직이 설정 화면에서 4% 를 보고, 그대로 저장하면 0.04 로 덮인다.
 */
export const parseSettingNumber = (
  value: string | null | undefined,
  fallback: string
): number => {
  const raw = (value ?? "").trim();
  const parsed = raw === "" ? Number.NaN : Number(raw);
  return Number.isFinite(parsed) ? parsed : Number(fallback);
};

/** JSON 배열로 굳혀 둔 목록 설정을 줄 배열로. 깨진 값은 빈 목록으로 본다. */
export const parseLines = (value: string | null): string[] => {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter((row): row is string => typeof row === "string")
      : [];
  } catch {
    return [];
  }
};
